import { SouserBulletinGuard } from "@/shared/components/guards/SouserBulletinGuard";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { AuthLayout } from "@/layouts/AuthLayout";
import { AppLayout } from "@/layouts/AppLayout";
import { AuthGuard } from "@/features/auth/components/AuthGuard";
import { ModuleGuard } from "@/shared/components/guards/ModuleGuard";

// Auth pages
import { LoginPage } from "@/features/auth/pages/LoginPage";
import { RegisterPage } from "@/features/auth/pages/RegisterPage";
import { ForgotPasswordPage } from "@/features/auth/pages/ForgotPasswordPage";
import { ResetPasswordPage } from "@/features/auth/pages/ResetPasswordPage";
import { ActivateAccountPage } from "@/features/auth/pages/ActivateAccountPage";

// Public pages (no auth required)
import { AccountDeletionPage } from "@/features/auth/pages/AccountDeletionPage";

// App pages
import { HomePage } from "@/features/announcements/pages/HomePage";
import { AnnouncementEditPage } from "@/features/announcements/pages/AnnouncementEditPage";
import { AnnouncementManagementPage } from "@/features/announcements/pages/AnnouncementManagementPage";
import { AnnouncementRedirect } from "@/features/announcements/pages/AnnouncementRedirect";
import { InquiryPage } from "@/features/inquiries/pages/InquiryPage";
import { InquiryFormPage } from "@/features/inquiries/pages/InquiryFormPage";
import { ChatPage } from "@/features/chat/pages/ChatPage";
import { CallPage } from "@/features/call/pages/CallPage";
import { NotificationsPage } from "@/features/notifications/pages/NotificationsPage";
import { SettingsPage } from "@/features/profile/pages/SettingsPage";
import { PersonalInfoPage } from "@/features/profile/pages/PersonalInfoPage";
import { SouserProfilePage } from "@/features/profile/pages/SouserProfilePage";
import { VisaInfoPage } from "@/features/profile/pages/VisaInfoPage";
import { UserSearchPage } from "@/features/profile/pages/UserSearchPage";
import { SharepointPage } from "@/features/files/pages/SharepointPage";
import { ReturnHomeListPage } from "@/features/return-home/pages/ReturnHomeListPage";
import { ReturnHomeFormPage } from "@/features/return-home/pages/ReturnHomeFormPage";
import { ReturnHomeAuditPage } from "@/features/return-home/pages/ReturnHomeAuditPage";
import { InquiryAuditPage } from "@/features/inquiries/pages/InquiryAuditPage";
import { LeavePage } from "@/features/leave/pages/LeavePage";
import { LeaveApplicationPage } from "@/features/leave/pages/LeaveApplicationPage";
import { LeaveFormBuilderPage } from "@/features/leave/pages/LeaveFormBuilderPage";
import { LeaveSubmissionsPage } from "@/features/leave/pages/LeaveSubmissionsPage";
import { CompanyPage } from "@/features/company/pages/CompanyPage";
import { SendingOrgPage } from "@/features/sending-org/pages/SendingOrgPage";
import { VisaListPage } from "@/features/visa-list/pages/VisaListPage";
import { IssueTypePage } from "@/features/issue-type/pages/IssueTypePage";
import { RoleManagementPage } from "@/features/admin/pages/RoleManagementPage";
import { OtherSetupPage } from "@/features/other-setup/pages/OtherSetupPage";
import { ChatTemplatesPage } from "@/features/other-setup/pages/ChatTemplatesPage";
import { UploadProfileVisaPage } from "@/features/other-setup/pages/UploadProfileVisaPage";
import TasksPage from "@/features/tasks/pages/TasksPage";
import { AssessmentListPage } from "@/features/assessments/pages/AssessmentListPage";
import { AssessmentBuilderPage } from "@/features/assessments/pages/AssessmentBuilderPage";
import { AssessmentResultsPage } from "@/features/assessments/pages/AssessmentResultsPage";
import { AppSupportPage } from "@/features/app-support/pages/AppSupportPage";
import { AppSupportFormPage } from "@/features/app-support/pages/AppSupportFormPage";

export const router = createBrowserRouter([
  // Public auth routes
  {
    element: <AuthLayout />,
    children: [
      { path: "/login",            element: <LoginPage /> },
      { path: "/register",         element: <RegisterPage /> },
      { path: "/forgot-password",  element: <ForgotPasswordPage /> },
    ],
  },

  // Public standalone pages (no auth required)
  { path: "/account/delete", element: <AccountDeletionPage /> },

  // Protected app routes
  {
    element: <AuthGuard />,
    children: [
      { path: "/activate", element: <ActivateAccountPage /> },
      {
        element: <AppLayout />,
        children: [
          // ── All authenticated users ─────────────────────────────────────
          { path: "/",                         element: <SouserBulletinGuard><HomePage /></SouserBulletinGuard> },
          { path: "/announcement/:id",         element: <AnnouncementRedirect /> },
          { path: "/tasks",                    element: <TasksPage /> },
          { path: "/chat",                     element: <ChatPage /> },
          { path: "/call/:callId?",            element: <CallPage /> },
          { path: "/notifications",            element: <NotificationsPage /> },
          { path: "/settings",                  element: <SettingsPage /> },
          { path: "/settings/reset-password",  element: <ResetPasswordPage /> },
          { path: "/settings/personal-info",   element: <PersonalInfoPage /> },
          { path: "/settings/souser-profile",  element: <SouserProfilePage /> },

          // ── announcements module ─────────────────────────────────────────
          {
            element: <ModuleGuard module="announcements" level="read" />,
            children: [
              { path: "/announcements",              element: <AnnouncementManagementPage /> },
              {
                element: <ModuleGuard module="announcements" level="write" redirectTo="/announcements" />,
                children: [
                  { path: "/announcements/new",      element: <AnnouncementEditPage /> },
                  { path: "/announcements/edit/:id", element: <AnnouncementEditPage /> },
                ],
              },
            ],
          },

          // ── inquiries module ─────────────────────────────────────────────
          {
            element: <ModuleGuard module="inquiries" level="read" />,
            children: [
              { path: "/inquiries",       element: <InquiryPage /> },
              { path: "/inquiries/new",   element: <InquiryFormPage /> },
              { path: "/inquiries/audit", element: <InquiryAuditPage /> },
            ],
          },

          // ── leave module ─────────────────────────────────────────────────
          {
            element: <ModuleGuard module="leave" level="read" />,
            children: [
              { path: "/leave",             element: <LeavePage /> },
              { path: "/leave/apply",       element: <LeaveApplicationPage /> },
              { path: "/leave/submissions", element: <LeaveSubmissionsPage /> },
              {
                element: <ModuleGuard module="leave" level="write" redirectTo="/leave" />,
                children: [
                  { path: "/leave/builder", element: <LeaveFormBuilderPage /> },
                ],
              },
            ],
          },

          // ── flight module ─────────────────────────────────────────────────
          {
            element: <ModuleGuard module="flight" level="read" />,
            children: [
              { path: "/return-home",       element: <ReturnHomeListPage /> },
              { path: "/return-home/new",   element: <ReturnHomeFormPage /> },
              { path: "/return-home/:id",   element: <ReturnHomeFormPage /> },
              { path: "/return-home/audit", element: <ReturnHomeAuditPage /> },
            ],
          },

          // ── visa module ──────────────────────────────────────────────────
          {
            element: <ModuleGuard module="visa" level="read" />,
            children: [
              { path: "/settings/visa-info",         element: <VisaInfoPage /> },
              { path: "/settings/visa-info/:userId", element: <VisaInfoPage /> },
              { path: "/visa-list",                  element: <VisaListPage /> },
              { path: "/issue-types",                element: <IssueTypePage /> },
            ],
          },

          // ── profile module ───────────────────────────────────────────────
          {
            element: <ModuleGuard module="profile" level="read" />,
            children: [
              { path: "/settings/personal-info/:userId", element: <PersonalInfoPage /> },
              { path: "/users",                          element: <UserSearchPage /> },
            ],
          },

          // ── other setup (hub page) ───────────────────────────────────────
          {
            element: <ModuleGuard module="company" level="read" />,
            children: [
              { path: "/other-setup",          element: <OtherSetupPage /> },
              { path: "/companies",            element: <CompanyPage /> },
              { path: "/sending-orgs",         element: <SendingOrgPage /> },
              { path: "/chat-templates",       element: <ChatTemplatesPage /> },
              { path: "/upload-profile-visa",  element: <UploadProfileVisaPage /> },
            ],
          },

          // ── sharepoint module ────────────────────────────────────────────
          {
            element: <ModuleGuard module="sharepoint" level="read" />,
            children: [
              { path: "/files",           element: <SharepointPage /> },
              { path: "/files/:folderId", element: <SharepointPage /> },
            ],
          },

          // ── assessments module ───────────────────────────────────────────
          {
            element: <ModuleGuard module="assessments" level="read" />,
            children: [
              { path: "/assessments",             element: <AssessmentListPage /> },
              { path: "/assessments/:id/results", element: <AssessmentResultsPage /> },
              {
                element: <ModuleGuard module="assessments" level="write" redirectTo="/assessments" />,
                children: [
                  { path: "/assessments/new",      element: <AssessmentBuilderPage /> },
                  { path: "/assessments/:id/edit", element: <AssessmentBuilderPage /> },
                ],
              },
            ],
          },

          // ── app support module ───────────────────────────────────────────
          // All authenticated users can access /support (data scoped by API).
          // /support/new is open to all users — no ModuleGuard needed.
          { path: "/support",     element: <AppSupportPage /> },
          { path: "/support/new", element: <AppSupportFormPage /> },

          // ── role management ──────────────────────────────────────────────
          {
            element: <ModuleGuard module="role_management" level="write" />,
            children: [
              { path: "/access/roles", element: <RoleManagementPage /> },
            ],
          },
        ],
      },
    ],
  },

  // Catch-all
  { path: "*", element: <Navigate to="/" replace /> },
]);
